/**
 * This file is based on code from the placemark repository
 * (https://github.com/placemark/placemark), licensed under the MIT License.
 *
 * Modifications made by ACT Corp are proprietary
 */
import { ConvertResult, getExtension } from "./utils"
//import { KML } from "./kml";
//import { KMZ } from "./kmz";
//import { TCX } from "./tcx";
//import { GPX } from "./gpx";
import { CSV } from "./csv";
//import { OSM } from "./osm";
//import { XLS } from "./xls";
//import { BBOX } from "./bbox";
//import { Polyline } from "./polyline";
import { GeoJSON } from "./geojson";
//import { GeoJSONL } from "./geojsonl";
import { Shapefile } from "./shapefile";
import { CZML } from './czml';
//import { TopoJSON } from "./topojson";
import { GeoTIFF } from "./geotiff";
//import { EXIF } from "./exif";
//import { WKT } from "./wkt";
//import { GTFS } from "./gtfs";
//import { CoordinateString } from "./coordinate_string";
//import { FlatGeobuf } from "./flatgeobuf";

import { isPlainObject } from "lodash-es";
import { JsonObject, JsonValue } from "type-fest";
import { ProxyMarked } from "comlink";
import { isArray } from "lodash-es";

type GeoJSONTypeList = GeoJSON.GeoJSON["type"][];

const geojsonTypes: GeoJSONTypeList = [
  "FeatureCollection",
  "Feature",
  "Point",
  "MultiPoint",
  "Polygon",
  "MultiPolygon",
  "LineString",
  "MultiLineString",
  "GeometryCollection",
];
export const GEOJSON_TYPES: Set<string> = new Set(geojsonTypes);

export enum GeocodingBehavior {
  NULL_GEOMETRY,
  DISCARD,
}

export interface ExportedData {
  extensions: FileType["extensions"];
  result: {
    blob: Blob;
    name: string;
  };
}

export const CSV_DELIMITERS = [
  { value: ",", label: "," },
  { value: ";", label: ";" },
  { value: "\t", label: "Tab" },
] as const;

export const CSV_KINDS = [
  {
    value: "lonlat",
    label: "Coordinates",
  },
  { value: "wkt", label: "WKT Column" },
  { value: "geojson", label: "GeoJSON Column" },
  { value: "polyline", label: "Encoded polylines" },
] as const;

export const DEFAULT_IMPORT_OPTIONS: Omit<ImportOptions, "type"> = {
  coordinateStringOptions: {
    order: "LONLAT",
  },
  removeCoincidents: true,
  csvOptions: {
    sheet: "",
    longitudeHeader: "",
    latitudeHeader: "",
    geometryHeader: "",
    kind: "lonlat",
    autoType: true,
    delimiter: ","
  },
} as const;

/**
 * Import options with object type.
 */
export interface ImportOptions {
  type: FileType["id"];
  toast?: boolean;
  coordinateStringOptions: {
    order: "LONLAT" | "LATLON";
  };
  lonlatProjection?: string;
  removeCoincidents?: boolean;
  csvOptions: {
    // For XLSX, technically. Shoving it in here.
    sheet: string;
    // Would be nice to keep that consistent.
    longitudeHeader: string | null;
    latitudeHeader: string | null;
    /** For WKT / GeoJSON kind */
    geometryHeader: string | null;
    kind: (typeof CSV_KINDS)[number]["value"];
    delimiter: (typeof CSV_DELIMITERS)[number]["value"];
    autoType: boolean;
  };
}

export const COORDINATE_STRING_ORDERS: Array<{
  value: ImportOptions["coordinateStringOptions"]["order"];
  label: string;
}> = [
  {
    value: "LONLAT",
    label: "Longitude, Latitude",
  },
  {
    value: "LATLON",
    label: "Latitude, longitude",
  },
];

export type Winding = "RFC7946" | "d3";

export interface ExportOptions {
  type: FileType["id"];
  folderId: string | null;
  geojsonOptions?: {
    winding: Winding;
    truncate: boolean;
    addBboxes: boolean;
    indent: boolean;
    includeId: boolean;
  };
  csvOptions?: ImportOptions["csvOptions"];
  lonlatProjection?: string;
}

export const DEFAULT_EXPORT_GEOJSON_OPTIONS: ExportOptions["geojsonOptions"] = {
  winding: "RFC7946",
  truncate: true,
  addBboxes: false,
  indent: false,
  includeId: false,
};

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  geojsonOptions: DEFAULT_EXPORT_GEOJSON_OPTIONS,
  folderId: null,
  type: "geojson",
};

export interface ExportResult {
  blob: Blob;
  name: string;
}

export interface Progress {
  total: number;
  done: number;
}

export type RawProgressCb = (progress: Progress) => void;
export type ProgressCb = RawProgressCb & ProxyMarked;

export interface FileType {
  readonly id:
    | "geojson"
    | "geojsonl"
    | "kml"
    | "kmz"
    | "tcx"
    | "gpx"
    | "csv"
    | "polyline"
    | "geotiff"
    | "wkt"
    | "gtfs"
    | "topojson"
    | "exif"
    | "bbox"
    | "shapefile"
    | "coordinate-string"
    | "xls"
    | "flatgeobuf"
    | "osm"
    | "czml";
  readonly label: string | string[];
  readonly extensions: string[];
  readonly mimes: string[];
  readonly filenames: string[];
  forwardBinary?: (
    file: ArrayBuffer,
    options: ImportOptions,
    callback: ProgressCb
  ) => Promise<ConvertResult>;
  forwardString?: (
    file: string,
    options: ImportOptions,
    callback: ProgressCb
  ) => Promise<ConvertResult>;
  back?: (
    inputs: {
      geojson: GeoJSON.FeatureCollection;
      //featureMap: FeatureMap;
      //folderMap: FolderMap;
    },
    options: ExportOptions
  ) => Promise<ExportResult>;
}

export const FILE_TYPES = [
  GeoJSON,
  // KML,
  // KMZ,
  //  TCX,
  //  GPX,
  CSV,
  // XLS,
  // Polyline,
  GeoTIFF,
  // EXIF,
  // WKT,
  //GTFS,
  //TopoJSON,
  //GeoJSONL,
  //BBOX,
  Shapefile,
  //FlatGeobuf,
  //CoordinateString,
  //OSM,
  CZML
] as const;

function assertIsObject(obj: JsonValue): obj is JsonObject {
  return isPlainObject(obj);
}

async function detectJson(file: File) {
  const text = await file.text();
  const obj = await JSON.parse(text);
  if (isArray(obj) && obj?.[0]?.id === 'document') {
    return { ...DEFAULT_IMPORT_OPTIONS, type: CZML.id };
  }
  if (!assertIsObject(obj)) {
    throw new Error("Could not determine JSON type");
  }
  if (obj.type === "Topology") {
    // uncomment if supporting topojson
    //return { ...DEFAULT_IMPORT_OPTIONS, type: TopoJSON.id };
  } else if (typeof obj?.type === "string" && GEOJSON_TYPES.has(obj.type)) {
    return { ...DEFAULT_IMPORT_OPTIONS, type: GeoJSON.id };
  }
  throw new Error("Could not determine JSON type");
}

export function findType(typeStr: string) {
  const type = FILE_TYPES.find((fileType) => fileType.id === typeStr);
  if (!type) throw new Error("Type not found");
  return type;
}

export async function detectType(file: File) {
  const { name } = file;
  const ext = getExtension(name);

  if (ext === ".json") {
    return await detectJson(file);
  }

  for (const type of FILE_TYPES) {
    if (
      (ext && type.extensions.includes(ext)) ||
      type.mimes.includes(file.type) ||
      (type.filenames && type.filenames.includes(name))
    ) {
      return {
        ...DEFAULT_IMPORT_OPTIONS,
        type: type.id,
      };
    }
  }

  throw new Error("Could not detect file type");
}

export async function stringToGeoJSON(
  ...args: Parameters<NonNullable<FileType["forwardString"]>>
) {
  const driver = findType(args[1].type);
  if ("forwardString" in driver) {
    return await driver.forwardString(...args);
  }
  throw new Error("Unsupported driver");
}

export async function fileToGeoJSON(
  ...args: Parameters<NonNullable<FileType["forwardBinary"]>>
) {
  const driver = findType(args[1].type);
  if ("forwardBinary" in driver) {
    return await driver.forwardBinary(...args);
  }
  throw new Error("Unsupported driver");
}

export function importToExportOptions(
  options: ImportOptions
): ExportOptions | null {
  const driver = findType(options.type);
  if (!("back" in driver)) return null;

  return {
    type: options.type,
    folderId: null,
  };
}

/**
 * From a list of wrapped features,
 * produce an ExportedData object that can contain
 * the results of any format.
 */
/*
export function fromGeoJSON(
  { featureMap, folderMap }: SetOptional<Data, "selection">,
  exportOptions: ExportOptions
) {

      const type = findType(exportOptions.type);
      if (!("back" in type)) {
        throw new Error("Unexpected missing type");
      }

      const { filteredFeatures, filteredFolders } =
        UWrappedFeature.filterMapByFolder(
          featureMap,
          folderMap,
          exportOptions.folderId
        );

      const geojson = UWrappedFeature.toFeatureCollection(
        Array.from(filteredFeatures.values())
      );

      const result = await type.back(
          { geojson, featureMap: filteredFeatures, folderMap: filteredFolders },
          exportOptions
        );

      return {
        result,
        extensions: type.extensions,
      };
    }
}
    */
